/**
 * Force Calculation Utility
 * 
 * This utility provides a unified system for calculating different types of forces
 * based on sensor data, user weight, and added weights.
 */

export enum ForceCalculationMode {
  LIFTED_FORCE = 'lifted_force',    // userWeight + addedWeights - measuredForce
  MEASURED_FORCE = 'measured_force', // measuredForce (raw sensor data)
  ADDED_FORCE = 'added_force'       // measuredForce - userWeight
}

export interface ForceCalculationParams {
  measuredForce: number;
  userWeight: number;
  addedWeights: number;
  mode: ForceCalculationMode;
}

export interface ForceCalculationResult {
  value: number | 'MAX' | 'Out';
  rawValue: number;
  mode: ForceCalculationMode;
  calculation: string;
}

/**
 * Calculate force based on the specified mode and parameters
 */
export function calculateForce(params: ForceCalculationParams): ForceCalculationResult {
  const { measuredForce, userWeight, addedWeights, mode } = params;

  let calculatedForce: number;
  let calculation: string;

  switch (mode) {
    case ForceCalculationMode.LIFTED_FORCE:
      // Hangboard sensor: low reading = high pull. Do not apply 'Out'/'MAX' thresholds
      // because a near-zero sensor reading means the user is pulling close to full body weight.
      // 'Out': sensor reads > userWeight (impossible physically) → treat as 0 pull
      calculatedForce = userWeight + addedWeights - measuredForce;
      calculation = `${userWeight} + ${addedWeights} - ${measuredForce} = ${calculatedForce}`;
      return {
        value: Math.max(0, calculatedForce),
        rawValue: measuredForce,
        mode,
        calculation
      };

    case ForceCalculationMode.MEASURED_FORCE:
      // Direct sensor reading. Low values mean no force.
      if (measuredForce < 0.15) {
        return { value: 'Out', rawValue: measuredForce, mode, calculation: `measuredForce (${measuredForce}) < 0.15kg → Out` };
      }
      if (measuredForce < 3) {
        return { value: 'MAX', rawValue: measuredForce, mode, calculation: `measuredForce (${measuredForce}) < 3kg → MAX` };
      }
      calculatedForce = measuredForce;
      calculation = `measuredForce = ${measuredForce}`;
      break;

    case ForceCalculationMode.ADDED_FORCE:
      if (measuredForce < 0.15) {
        return { value: 'Out', rawValue: measuredForce, mode, calculation: `measuredForce (${measuredForce}) < 0.15kg → Out` };
      }
      calculatedForce = measuredForce - userWeight;
      calculation = `${measuredForce} - ${userWeight} = ${calculatedForce}`;
      break;

    default:
      // Default: treat as LIFTED_FORCE (hangboard is the standard Entralpi device)
      calculatedForce = userWeight + addedWeights - measuredForce;
      calculation = `default LIFTED_FORCE: ${userWeight} + ${addedWeights} - ${measuredForce} = ${calculatedForce}`;
  }

  return {
    value: Math.max(0, calculatedForce),
    rawValue: measuredForce,
    mode,
    calculation
  };
}

/**
 * Get the appropriate force calculation mode for a workout type
 */
export function getForceCalculationModeForWorkoutType(workoutType: string): ForceCalculationMode {
  switch (workoutType) {
    case 'hangboard':
      return ForceCalculationMode.LIFTED_FORCE;
    case 'portable':
      return ForceCalculationMode.MEASURED_FORCE;
    case 'lift':
      return ForceCalculationMode.ADDED_FORCE;
    default:
      return ForceCalculationMode.MEASURED_FORCE;
  }
}

/**
 * Format force value for display
 */
export function formatForceForDisplay(force: number | 'MAX' | 'Out', precision: number = 1): string {
  if (force === 'MAX' || force === 'Out') {
    return force;
  }
  
  return force.toFixed(precision);
}

/**
 * Get force value for chart display (converts 'MAX' to high value)
 */
export function getForceForChart(force: number | 'MAX' | 'Out'): number {
  if (force === 'MAX') {
    return 1000; // High value for chart display
  }
  if (force === 'Out') {
    return 0;
  }
  return force;
}
